import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { Subscription } from 'rxjs';
import { DollarRiskLevel, MaxRiskCeiling, PriceRange, RelativeRiskLevel, RiskSide } from './copilot-domain';
import { ALL_RISK_LEVELS_REQUEST, isMaxRiskCeilingValid, isPriceRangeValid, RELATIVE_RISK_LEVELS } from './copilot-validation';
import { TradingInstrument } from '../../trading-instruments/trading-instrument.class';
import { OptionExpirationDescriptor } from '../../shell-communication/shell-dto-protocol';
import { ExpectedMoveService } from '../../expected-move/expected-move.service';
import { AppSettingsUpdatedMessageTopic } from '../../app-settings/application-settings.service';
import { MessageBusService } from '../../message-bus.service';
import { DetectMethodChanges } from '../../utils';
import { convertOnNormalizedModeFlip } from '../../copilot-shared/normalized-raw-conversion';

// The Adjust questionnaire block: PriceRange + MinProfit + MaxRisk (FR-14..17). ADJUST TAB ONLY
// since the two-copilots reversal (CR-20260804-001 ruling AF): the Select tab embeds the Select
// Copilot's own questionnaire (copilot-shared/) and never reaches this component - the earlier
// note here that "the Select tab wraps it" described the pre-reversal wiring. Emits its current
// state + validity upward on every edit (parent gates RUN on valid). Presence+sanity only — the
// host never judges trade quality (the skill does).

// The four price-range forms. 'fillGap' is engine-only (CR-20260724-001) and offered on Adjust alone.
type PriceKind = 'bounded' | 'fillGap' | 'followThrough' | 'wall';

// The forms that take a start+end strike pair. Listed here rather than spelled out in the template's
// *ngIf, so adding a fifth form means editing one condition instead of hunting for it in markup.
// buildPriceRange still branches per kind — it has to emit a specific discriminant, not just know
// that a pair was used — so keep the two in step when a form is added.
function usesStrikePair(kind: PriceKind): boolean {
    return kind === 'bounded' || kind === 'fillGap';
}

export interface RiskInputsState {
    priceRange: PriceRange;
    minProfit: number;
    minProfitNormalized: boolean;   // discriminator: minProfit is a normalized value, not plain dollars
    maxRisk: MaxRiskCeiling;        // its dollar variant carries `normalized` + its own placement level (CR-20260819-002)
    // Which side(s) of the range the run may extend risk to, and whether that risk is halved
    // (owner, vrequest 2026-08-18). Both ride the wire since CR-20260818-001 iteration 3. The SIDE
    // qualifies BOTH max-risk arms (CR-20260819-002 R-4/R-9); the halving stays relative-only
    // (R-3: on a typed amount, "if you want half, just go and enter half") and travels false in
    // dollar mode.
    //
    // ⚠ They are reported at their DEFAULTS whenever their controls are not live - section hidden,
    // or relative Low - for the same reason `harvest` is: a value the trader cannot see must never
    // travel. See buildState for the exact gates.
    riskSide: RiskSide;
    halfRisk: boolean;              // true = apply half the 1:1 ceiling (half the requested minimum profit)
    harvest: boolean;               // Adjust only (allowHarvest); always false for a consumer that hides the control
    // CR-20260820-002. The Expected Move MODEL to measure the band with, or null on every other
    // form. Read HERE, beside the gate that decides whether the box is offered at all, so the
    // control and the courier can never disagree about it (the WI-7 rule).
    expectedRangeModel: { model: string, customPercent: number | null } | null;
    valid: boolean;
}

function orNaN(n: number | null): number {
    return n == null ? NaN : n;
}

@Component({
    selector: 'ets-hg-copilot-risk-inputs',
    templateUrl: 'hg-copilot-risk-inputs.component.html',
    styleUrls: ['./hg-copilot-risk-inputs.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class HgCopilotRiskInputsComponent implements OnInit, OnDestroy {
    // ⚠ `_changeDetector` IS REQUIRED BY NAME, not merely useful. @DetectMethodChanges resolves it
    // as `this['_changeDetector']` — a string index, so a component without one compiles perfectly
    // and then logs "No _changeDetector" at runtime while the OnPush view never refreshes. Renaming
    // this field, or dropping it, silently reinstates the stale-gate defect the decorator is here
    // to prevent.
    constructor(
        private readonly _changeDetector: ChangeDetectorRef,
        private readonly _expectedMove: ExpectedMoveService,
        private readonly _messageBus: MessageBusService) { }

    private _appSettingsSub: Subscription;

    @Output() stateChange = new EventEmitter<RiskInputsState>();

    // The underlying + the resolved expiration (from the tab's context date) that drive the strike-range
    // ets-strikes-dropdown inputs. Supplied by the parent tab; the dropdown is disabled until both are set.
    @Input() targetInstrument: TradingInstrument | null = null;
    @Input() expiration: OptionExpirationDescriptor | null = null;

    // Whether the Wall price-range option is offered. Adjust allows it (default); Select does NOT
    // (owner ruling 2026-06-22 — Wall is not a valid Select range), so the Select tab passes false
    // and the Wall button is filtered out of priceButtons.
    @Input() allowWall = true;

    // Fill-Gap form + Harvest Peaks command (CR-20260724-001). Both are served by the composition
    // engine, and this delivery routes only the ADJUST tab to it, so the Adjust tab opts IN.
    //
    // These default to FALSE while allowWall above defaults to TRUE, and that asymmetry is
    // deliberate: Wall is an older form the LLM path understands on every tab, so opting out is the
    // exception; these two are understood by the engine alone, so a consumer that has not said it is
    // engine-backed must not silently offer a control whose run the backend would refuse by name.
    @Input() allowFillGap = false;
    @Input() allowHarvest = false;

    // "Adjust to Expected Range" (CR-20260820-002). Opted IN for the same reason as the two above:
    // the band is measured by the composition engine, which only the Adjust tab reaches. Offering
    // it on Select would send a price-range form that tab's engine has never heard of.
    @Input() allowExpectedRange = false;

    // "All" — every relative level in one run, so the answers can be compared side by side (owner,
    // 2026-08-17). Opted IN for the same reason as the two above: it is served by the composition
    // engine's fan-out, which only the Adjust tab reaches. Offering it on Select would send a level
    // that tab's engine has never heard of.
    @Input() allowAllLevels = false;

    // CR-20260831-004: the fields that no longer match what the CURRENT run sent, diffed by the
    // orchestrator (hg-copilot-param-diff grammar) and forwarded by the Adjust tab. A FRESH object
    // per recompute is the OnPush wake-up. Null/empty = clean (or no run yet) — every cue off.
    @Input() changedKeys: { [key: string]: boolean } | null = null;

    // CR-20260901-001: the portfolio's default quantity, supplied by the Adjust tab off the grid
    // facade — the mode-flip handlers convert the typed amount with the value current AT THE FLIP
    // (owner ruling). Undefined/unknown qty = no conversion (the number is left as typed).
    @Input() defaultQty: number | null | undefined = null;

    // Template guard for the per-control [class.param-changed] bindings.
    isChanged(key: string): boolean {
        return !!(this.changedKeys && this.changedKeys[key]);
    }

    // ⚠ No engine/strictness/route controls belong here: the engine has ONE composer set
    // (2026-08-08) and clean delivery is its only mode. Do not reintroduce a composer switch, a
    // strictness checkbox, or a route toggle - the backend refuses retired directives by name.

    // Moderate is a REAL level with its own placement (CR-20260818-001 reversed the 2026-07-28
    // "accepted and ignored" ruling): it authorises the same depth as Aggressive and differs in
    // WHERE the brake lands - further out, found by the backend's crime-scene price walk. The
    // earlier note here that the engine authorises Moderate "the same zero depth as Low" described
    // the pre-reversal engine.

    // Price-range / max-risk / relative-level choices are SEGMENTED BUTTON SETS (dx-button-group),
    // not drop-downs (owner ruling 2026-06-22). dx-button-group renders item.text and keys on id.
    // Bracketed explanatory text was dropped — buttons carry the bare option name.
    // Order is the owner's (vrequest 2026-07-27): Fill Gap sits directly after Range, because the two
    // read as a pair — same strikes, different permission outside them.
    private readonly priceButtonsAll: { id: PriceKind; text: string }[] = [
        { id: 'bounded', text: 'Range' },
        { id: 'fillGap', text: 'Fill Gap' },
        { id: 'followThrough', text: 'Follow-Through' },
        { id: 'wall', text: 'Wall' }
    ];
    // Minimum Profit input MODE (vrequest 2026-07-20) — replaced a "Normalized" checkbox with a
    // mutually exclusive pair, so both meanings are named instead of only the checked one.
    // The "normalized" label is BARE: on the two-sided Follow-Through each side block is half the
    // form wide, and "Normalized (Def. Qty)" wrapped there — a two-line button taller than its
    // "Raw ($)" partner. "Raw ($)" keeps its bracket: it is short, and it still names the contrast.
    // The flag stays a boolean end-to-end — the wire field is unchanged.
    readonly minProfitModeButtons: { id: 'normalized' | 'raw'; text: string }[] = [
        { id: 'normalized', text: 'Normalized' },
        { id: 'raw', text: 'Raw ($)' }
    ];
    readonly maxRiskButtons: { id: 'dollar' | 'relative'; text: string }[] = [
        { id: 'dollar', text: 'Dollar ($)' },
        { id: 'relative', text: 'Relative Level' }
    ];
    // "All" sits AFTER the three appetites (owner's own order: "low, moderate, aggressive, and then
    // all") and only where the consumer has opted in — it is served by the composition engine's
    // fan-out, which the Select tab does not run.
    // MEMOIZED (CR-20260831-004 defect round 2). A getter that builds a FRESH array per call feeds
    // dx-button-group a new `items` reference on EVERY change-detection pass, and each items change
    // makes ButtonGroup fully re-render — re-feeding its self-synced stale `selectedItems` object
    // list into the new button collection (button_group.js _renderButtons), which is the root of
    // the owner-reported double-highlight / lost-highlight selection bug. Stable reference until
    // the gate actually flips = no re-render churn, no stale re-feed.
    private _relativeLevelButtonsKey: boolean | undefined = undefined;
    private _relativeLevelButtonsCache: { id: RelativeRiskLevel; text: string }[] = [];

    get relativeLevelButtons(): { id: RelativeRiskLevel; text: string }[] {
        if (this.allowAllLevels !== this._relativeLevelButtonsKey) {
            this._relativeLevelButtonsKey = this.allowAllLevels;
            const buttons = RELATIVE_RISK_LEVELS.map(l => ({ id: l, text: l }));
            if (this.allowAllLevels) {
                buttons.push({ id: ALL_RISK_LEVELS_REQUEST, text: 'All' });
            }
            this._relativeLevelButtonsCache = buttons;
        }
        return this._relativeLevelButtonsCache;
    }

    // The rebuild-survivable selection pair (see selectedItemsFor): [selectedItems] bound beside
    // [selectedItemKeys] so a rare items rebuild resolves selection from OUR current object, never
    // from ButtonGroup's stale self-synced list.
    get relativeLevelSelectedItems(): { id: RelativeRiskLevel; text: string }[] {
        return this.selectedItemsFor('relativeLevel', this.relativeLevelButtons, this.relativeLevel);
    }

    // RISK SIDE (owner, vrequest 2026-08-18) — which edge(s) of the range the brake may sell beyond.
    // A segmented set like every other choice here, and MUTUALLY EXCLUSIVE by his instruction ("all
    // these options must be mutual exclusive, so you can only select one of them"), which is what
    // selectionMode="single" already gives. ONE set serving BOTH max-risk arms since CR-20260819-002
    // (R-4): the field is shared, so the choice survives switching arms.
    //
    // 'Auto' is REAL (CR-20260819-001): the backend weighs each edge's risk structure by the total
    // credit it pays and resolves the side before composing — on both arms since CR-20260819-002.
    readonly riskSideButtons: { id: RiskSide; text: string }[] = [
        { id: 'up', text: 'Up' },
        { id: 'down', text: 'Down' },
        { id: 'both', text: 'Both' },
        { id: 'auto', text: 'Auto' }
    ];

    // The Dollar arm's level set (CR-20260819-002 R-2): [Moderate | Aggressive], plus All where the
    // consumer opted into the fan-out — the same allowAllLevels gate the relative set uses, for the
    // same reason (the Select-side engines do not know the value). NO Low: "low assumes no risk, so
    // it has to be moderate or aggressive placement" — a $0 amount is the dollar spelling of
    // no-risk. Bare option names per the 2026-06-22 button-set rule.
    // Memoized + selection-paired for the same reasons as relativeLevelButtons above.
    private _dollarLevelButtonsKey: boolean | undefined = undefined;
    private _dollarLevelButtonsCache: { id: DollarRiskLevel; text: string }[] = [];

    get dollarLevelButtons(): { id: DollarRiskLevel; text: string }[] {
        if (this.allowAllLevels !== this._dollarLevelButtonsKey) {
            this._dollarLevelButtonsKey = this.allowAllLevels;
            const buttons: { id: DollarRiskLevel; text: string }[] = [
                { id: 'Moderate', text: 'Moderate' },
                { id: 'Aggressive', text: 'Aggressive' }
            ];
            if (this.allowAllLevels) {
                buttons.push({ id: 'All', text: 'All' });
            }
            this._dollarLevelButtonsCache = buttons;
        }
        return this._dollarLevelButtonsCache;
    }

    get dollarLevelSelectedItems(): { id: DollarRiskLevel; text: string }[] {
        return this.selectedItemsFor('dollarLevel', this.dollarLevelButtons, this.dollarLevel);
    }

    // ⚠ THE Up|Down DIRECTION SET IS GONE (CR-20260824-015): the two-sided Follow-Through states
    // its sides as two independent blocks, so there is no direction to pick and nothing to infer.
    // Do not reintroduce a direction control - the backend refuses the direction-era request by
    // name.

    // Wall hidden on Select; Fill Gap hidden anywhere that has not opted in.
    // Memoized + selection-paired for the same reasons as relativeLevelButtons above.
    private _priceButtonsKey = '';
    private _priceButtonsCache: { id: PriceKind; text: string }[] = [];

    get priceButtons(): { id: PriceKind; text: string }[] {
        const key = this.allowWall + '|' + this.allowFillGap;
        if (key !== this._priceButtonsKey) {
            this._priceButtonsKey = key;
            this._priceButtonsCache = this.priceButtonsAll.filter(b => {
                if (b.id === 'wall') {
                    return this.allowWall;
                }
                if (b.id === 'fillGap') {
                    return this.allowFillGap;
                }
                return true;
            });
        }
        return this._priceButtonsCache;
    }

    get priceSelectedItems(): { id: PriceKind; text: string }[] {
        return this.selectedItemsFor('priceKind', this.priceButtons, this.priceKind);
    }

    // ---- rebuild-survivable dx-button-group selection (CR-20260831-004 defect round 2) --------
    // ButtonGroup self-syncs a `selectedItems` OBJECT list and re-feeds it into the fresh button
    // collection on every items re-render — stale objects from the PREVIOUS items array, fighting
    // our [selectedItemKeys]. Outcome: two segments selected at once, or none while one is chosen
    // (the owner-reported mutual-exclusivity bug). Binding [selectedItems] to the CURRENT items
    // array's selected object makes both sources agree by construction. Memoized per group on
    // (items reference, selected id) so the OnPush bindings stay reference-stable between changes.
    private _selCache: { [group: string]: { itemsRef: any; id: any; value: any[] } } = {};

    private selectedItemsFor<T extends { id: any }>(group: string, items: T[], id: any): T[] {
        const hit = this._selCache[group];
        if (hit && hit.itemsRef === items && hit.id === id) {
            return hit.value;
        }
        const value = items.filter(b => b.id === id);
        this._selCache[group] = { itemsRef: items, id: id, value: value };
        return value;
    }

    // True when the selected form takes a start+end strike pair (Range or Fill Gap) — the template's
    // single condition for showing the strike controls.
    get showStrikePair(): boolean {
        return usesStrikePair(this.priceKind);
    }

    // THE EXPECTED-RANGE BOX BELONGS TO THE RANGE FORM ALONE (owner, 2026-08-20: "we now don't
    // think about fill gap and the other modes... only range mode is within the scope of this work
    // right now") — and it is offered only where the settings can actually produce a band, because
    // a box that resolves to nothing is a run that refuses after the click.
    get showExpectedRange(): boolean {
        return this.allowExpectedRange && this.priceKind === 'bounded';
    }

    // ⚠ DISABLED IN PLACE, NOT HIDDEN (owner ruling, 2026-08-19). The standing house preference,
    // and the right one here: Expected Move is switched off in a DIFFERENT dialog, so a box that
    // vanished would leave the trader with no way to learn the option exists, let alone that a
    // setting elsewhere is why it does not.
    //
    // Gated on the enable checkbox ALONE, exactly as the Select side is (owner ruling D2) — NOT on
    // `Where To Show`. The two predicates answer different questions: where the overlay is drawn is
    // not whether a band can be measured.
    //
    // ⚠ A FIELD REFRESHED FROM THE BUS, NOT A GETTER. This component is OnPush, so a getter reading
    // the service would only be re-read when something else happened to trigger change detection —
    // the trader would save App Settings and the box would stay stale until an unrelated click.
    // That is CR-20260727-001's R1 defect verbatim ("the gate was stale until an unrelated
    // redraw"); it cost a smoke round there and is not worth paying for twice.
    expectedRangeAvailable = false;

    // Locked whenever the settings cannot measure a band. Combined with the auto-release in
    // onAppSettingsUpdated, "disabled" here always means "disabled AND unchecked" — the trader can
    // never be shut inside a mode that has gone away.
    get expectedRangeBoxLocked(): boolean {
        return !this.expectedRangeAvailable;
    }

    // ⚠ THE PAIR IS DEAD ON THE TRADER'S CHOICE, NOT ON THE SETTING. The Start/End block is SHARED
    // with Fill Gap (one block, deliberately, so the controls cannot drift apart per form), so the
    // priceKind term is load-bearing: without it, a trader who ticked the box on Range and moved to
    // Fill Gap would find two dead dropdowns, no visible control to revive them, and RUN disabled
    // with nothing on screen to fix — the exact trap buildMaxRisk already guards against.
    //
    // ⚠ AVAILABILITY IS DELIBERATELY NOT A TERM HERE. When the setting goes off under a ticked box
    // the selection STAYS (owner ruling CR-20260727-001 R2: a selected Expected Range stays
    // selected, the questionnaire simply becomes unrunnable, and re-enabling restores it). Letting
    // the pair come back to life instead would leave a ticked box sitting above two live dropdowns,
    // which states two different answers to the same question at once.
    get strikePairDisabled(): boolean {
        return this.showExpectedRange && this.useExpectedRange;
    }

    // The flag as it actually ACTS: ONE predicate behind the emitted state and the wire, so the
    // control, the RUN gate and the courier can never disagree (the WI-7 rule).
    //
    // ⚠ The availability term is a BACKSTOP, not the live rule. The rule is the auto-release in
    // onAppSettingsUpdated: a box that goes disabled unchecks itself, so a ticked box always implies
    // an available setting and this term should never be what makes the difference. It stays because
    // the alternative is couriering a band with no model behind it — the backend would read the
    // absent model as "ATM Straddle" and run a scale nobody chose, with only a log line saying so.
    private get useExpectedRangeEffective(): boolean {
        return this.strikePairDisabled && this.expectedRangeAvailable;
    }

    ngOnInit(): void {
        // SEED BEFORE SUBSCRIBING: the bus reports CHANGES only, so without this the dialog would
        // open assuming the setting is on and stay wrong until the trader next saved App Settings.
        this.expectedRangeAvailable = this._expectedMove.isExpectedRangeUsableForCoPilot();

        this._appSettingsSub = this._messageBus.of(AppSettingsUpdatedMessageTopic)
            .subscribe(() => this.onAppSettingsUpdated());
    }

    ngOnDestroy(): void {
        if (this._appSettingsSub) {
            this._appSettingsSub.unsubscribe();
        }
    }

    // Fires on EVERY app-settings save (the bus carries no payload); recomputing one boolean is
    // cheap enough that filtering would cost more than it saves.
    //
    // ⚠ GOING DISABLED ALSO UNCHECKS IT (owner, 2026-08-20): *"as soon as it gets disabled, it must
    // uncheck itself, releasing the range controls for selection"*. That is what makes the disabled
    // state safe — the box holds the strike pair dead, so a box that stayed ticked while unclickable
    // would leave the Range form with no live control at all and RUN gated off by the NaN edges.
    // Releasing the tick hands the pair straight back, empty and live, and the trader types a range
    // instead. Nothing is stranded and there is nothing to explain.
    //
    // ⚠ THIS DEPARTS FROM THE OPG SIDE, deliberately and by his ruling. There (CR-20260727-001 R2)
    // the selection SURVIVES the setting going away and the questionnaire simply becomes unrunnable.
    // Do not "restore parity" — the two panels now answer this differently on purpose.
    //
    // The strikes are NOT restored: they were cleared when the box was ticked, so the pair comes
    // back empty. That is the honest state — the trader has not chosen a range yet, and inventing
    // the one they had before the tick would be a range nobody currently asked for.
    @DetectMethodChanges()
    private onAppSettingsUpdated(): void {
        this.expectedRangeAvailable = this._expectedMove.isExpectedRangeUsableForCoPilot();

        if (!this.expectedRangeAvailable) {
            this.useExpectedRange = false;
        }

        this.emit();
    }

    // THE RELATIVE LEVELS BELONG TO THE RANGE FORM ALONE (owner, 2026-08-17): "if I go to fill the
    // gap, there's no such choices as low, moderate, aggressive, so they shouldn't show up... when
    // you click on the others, it shouldn't show up because it throws up an illusion that there's
    // difference between those trades and there's not."
    //
    // Why each of the other three is excluded, in his words: Fill Gap has the rule not to destroy
    // other trades, so it may not take on risk elsewhere at all; a Follow-Through cannot carry risk
    // "because otherwise it can't be follow through"; and a Wall has no sinkholes by construction, so
    // there is nothing for a level to choose between.
    //
    // ⚠ HIDDEN, NOT DISABLED, AND THAT IS THE EXCEPTION. The standing preference is to disable a
    // control in place so the trader can still see the choice exists. He ruled the opposite here by
    // name — "they shouldn't show up" — because a visible-but-dead control is the illusion itself.
    //
    // ⚠ THE WHOLE SECTION NOW GOES WITH THEM, THE DOLLAR ARM INCLUDED (owner, vrequest 2026-08-18:
    // "max risk section should only be visible in the range mode. If we have follow through, wall,
    // we don't see it there"). This REPLACES the earlier reading that left the Dollar arm on every
    // form — it is the same question about the same thing, and half a question is not less illusion
    // than a whole one. See showMaxRisk, which is the condition the template now hangs the fieldset
    // on, and buildMaxRisk for what travels while it is hidden.
    get showRelativeLevels(): boolean {
        return this.showMaxRisk && this.maxRiskKind === 'relative';
    }

    // THE MAX RISK SECTION BELONGS TO THE RANGE FORM ALONE (owner, vrequest 2026-08-18) — legend,
    // mode toggle, levels, risk side and half risk all appear and disappear together. One getter, so
    // the section cannot come apart form by form.
    get showMaxRisk(): boolean {
        return this.priceKind === 'bounded';
    }

    // THE DOLLAR ARM CARRIES LEVELS AND A RISK SIDE OF ITS OWN (CR-20260819-002 R-2/R-4): the
    // Dollar tab shows [Moderate | Aggressive (| All)] plus the Risk Side set. No ½ Half Risk
    // there (R-3: "if you want half, just go and enter half") and no watermark — the watermark is
    // the relative arm's Low state, and the dollar arm has no Low to be in.
    get showDollarLevels(): boolean {
        return this.showMaxRisk && this.maxRiskKind === 'dollar';
    }

    // LOW HAS NO RISK TO TAKE A SIDE ON (owner, vrequest 2026-08-19): "the only level that contains
    // no risk is low, which means this panel makes no sense when low is selected". So on Low the
    // Risk Side set and ½ Half Risk go, and a watermark takes their place.
    //
    // ⚠ This is the ONLY level excluded. Moderate keeps the controls by his instruction ("when a
    // user switches to any other mode, which is a moderate, aggressive or all, this should show
    // up") — and Moderate genuinely takes risk (CR-20260818-001 gave it its own placement at the
    // same depth as Aggressive), so there is a side for it to answer. He was asked about the level
    // list and named three.
    //
    // ⚠ MUTED, NOT REMOVED. Unlike showMaxRisk / showRelativeLevels above — which take their
    // controls out of the layout — this one only takes away VISIBILITY: he asked for the space to
    // be kept so the panel does not change height ("I don't want the window to jump up and down").
    // The template keeps the controls mounted and overlays the watermark on them.
    get showRiskSideControls(): boolean {
        return this.showRelativeLevels && this.relativeLevel !== 'Low';
    }

    priceKind: PriceKind = 'bounded';
    startStrike: number | null = null;
    endStrike: number | null = null;
    // Harvest Peaks — OFF by default. Off is the conservative reading of an unanswered question:
    // it leaves profit the trader already owns untouched.
    harvest = false;

    // ---- The two-sided Follow-Through (CR-20260824-015) ----
    // Two independent side blocks, each with its own enable, strike, Harvest and minimum (mode +
    // amount, defaulting like the global minimum's controls: normalized, empty). One global
    // Close Remaining Positions toggle, OFF by default — "leave as is, don't touch it" is the
    // owner's stated default, superseding the old form's unconditional cut.
    ftUpEnabled = false;
    ftUpStrike: number | null = null;
    ftUpHarvest = false;
    ftUpMinProfit: number | null = null;
    ftUpMinProfitNormalized = true;
    ftDownEnabled = false;
    ftDownStrike: number | null = null;
    ftDownHarvest = false;
    ftDownMinProfit: number | null = null;
    ftDownMinProfitNormalized = true;
    ftCloseRemaining = false;

    get isFollowThrough(): boolean {
        return this.priceKind === 'followThrough';
    }

    // Presentation adapters for the per-side Minimum Profit selectors — the booleans stay the
    // state of record, exactly as the global selector's adapter works.
    get ftUpMinProfitMode(): 'normalized' | 'raw' {
        return this.ftUpMinProfitNormalized ? 'normalized' : 'raw';
    }

    get ftDownMinProfitMode(): 'normalized' | 'raw' {
        return this.ftDownMinProfitNormalized ? 'normalized' : 'raw';
    }

    // THE HINT LINE (approved on the mockup): what "remaining" resolves to under the current
    // sides, in one sentence, so the toggle's subject is never a guess. Reworded live as the
    // sides change; the strikes are read off the dropdowns as picked.
    get ftRemainingHint(): string {
        const up = this.ftUpEnabled;
        const down = this.ftDownEnabled;

        if (!up && !down) {
            return 'Remaining: Enable at Least One Side';
        }

        if (up && !down) {
            return this.ftUpStrike != null
                ? 'Remaining: Everything Below ' + this.ftUpStrike
                : 'Remaining: Everything Below the Up Start Strike';
        }

        if (!up && down) {
            return this.ftDownStrike != null
                ? 'Remaining: Everything Above ' + this.ftDownStrike
                : 'Remaining: Everything Above the Down Start Strike';
        }

        if (this.ftUpStrike != null && this.ftDownStrike != null) {
            if (this.ftDownStrike > this.ftUpStrike) {
                return 'The Down Start Strike is Above the Up One - the Sides Cannot Overlap';
            }

            if (this.ftDownStrike === this.ftUpStrike) {
                return 'Remaining: Nothing - the Sides Meet, the Toggle Has Nothing to Act on';
            }

            return 'Remaining: the Band Between ' + this.ftDownStrike + ' And ' + this.ftUpStrike;
        }

        return 'Remaining: the Band Between the Two Start Strikes';
    }

    // "Adjust to Expected Range" — OFF by default, so an untouched panel behaves exactly as it did
    // before this control existed and the trader's own strikes stay the shipped answer.
    useExpectedRange = false;

    minProfit: number | null = null;
    // Minimum Profit mode — NORMALIZED by default (vrequest 2026-07-20, was plain dollars). This
    // component takes no state @Input, so these initializers ARE the effective control defaults;
    // the Select tab's _risk seed mirrors them and must be changed with them.
    minProfitNormalized = true;

    // Max Risk mode — RELATIVE by default (owner, 2026-08-02; was dollar). Applies wherever this
    // block is used: Select and Adjust both. The level below defaults Low, so the control is
    // complete out of the box instead of waiting on a dollar amount.
    maxRiskKind: 'dollar' | 'relative' = 'relative';
    dollarAmount: number | null = null;
    dollarNormalized = true;       // "Normalized" checkbox next to Maximum Risk in Dollars — CHECKED by default (vrequest 2026-07-20)
    relativeLevel: RelativeRiskLevel = 'Low';
    // The dollar arm's OWN level field (CR-20260819-002) — per-arm state, so switching arms keeps
    // each arm's last choice. Default AGGRESSIVE (ratified): pure geometry, so a first Dollar run
    // never waits on a quote acquisition. The relative arm keeps its own field and its Low default.
    dollarLevel: DollarRiskLevel = 'Aggressive';

    // Risk side — BOTH by default, because both is what the engine has always done (CR-20260818-001
    // R-9 hardcodes it), so the control opens on the behaviour the trader already has.
    riskSide: RiskSide = 'both';
    // Half risk — OFF by default = FULL risk, which is the 1:1 ceiling (risk equals the requested
    // minimum profit). Checked halves it. Off is the shipped behaviour, so an untouched control
    // changes nothing.
    halfRisk = false;

    // Presentation adapter for the Minimum Profit segmented selector — the boolean above stays the
    // state of record (and the wire shape), so there is no second source of truth to drift.
    get minProfitMode(): 'normalized' | 'raw' {
        return this.minProfitNormalized ? 'normalized' : 'raw';
    }

    emit(): void {
        this.stateChange.emit(this.buildState());
    }

    // dx-button-group click handlers — set the discriminant from the clicked item, then re-emit
    // (same state/validity flow the old select-box (onValueChanged) used, so RUN gating is unchanged).
    onPriceKind(e: { itemData: { id: PriceKind } }): void {
        this.priceKind = e.itemData.id;
        // The pair is SHARED with Fill Gap, so arriving at Range can find strikes typed on
        // another form sitting under a ticked box. See clearStrikesWhilePairIsDead. (The
        // Follow-Through's sides carry their own strikes since CR-20260824-015.)
        this.clearStrikesWhilePairIsDead();
        this.emit();
    }

    onMinProfitMode(e: { itemData: { id: 'normalized' | 'raw' } }): void {
        const toNormalized = e.itemData.id === 'normalized';
        // CR-20260901-001: an actual FLIP converts the typed amount so it keeps meaning the same
        // thing (a re-click on the already-selected mode converts nothing — dx-button-group fires
        // onItemClick for those too).
        if (toNormalized !== this.minProfitNormalized) {
            this.minProfit = convertOnNormalizedModeFlip(this.minProfit, toNormalized, this.defaultQty);
        }
        this.minProfitNormalized = toNormalized;
        this.emit();
    }

    // CR-20260901-001: the max-risk "Normalized" checkbox gets its OWN handler (it bound plain
    // emit() before) because a mode flip must also CONVERT the typed dollar amount — [(value)] has
    // already flipped dollarNormalized by the time this runs, so convert toward the new state.
    onDollarNormalizedEdited(): void {
        this.dollarAmount = convertOnNormalizedModeFlip(this.dollarAmount, this.dollarNormalized, this.defaultQty);
        this.emit();
    }

    onMaxRiskKind(e: { itemData: { id: 'dollar' | 'relative' } }): void {
        this.maxRiskKind = e.itemData.id;
        this.emit();
    }

    onRelativeLevel(e: { itemData: { id: RelativeRiskLevel } }): void {
        this.relativeLevel = e.itemData.id;
        this.emit();
    }

    onDollarLevel(e: { itemData: { id: DollarRiskLevel } }): void {
        this.dollarLevel = e.itemData.id;
        this.emit();
    }

    onRiskSide(e: { itemData: { id: RiskSide } }): void {
        this.riskSide = e.itemData.id;
        this.emit();
    }

    // ---- The two-sided Follow-Through's handlers (CR-20260824-015) ----
    // dx-check-box / dx-button-group handlers in the same idiom as every control above. The side
    // fields are NOT cleared on disable — re-enabling restores what was typed ("maintain state",
    // the standing preference) — and buildPriceRange simply does not read a disabled side, so a
    // value the trader cannot see never travels.
    onFtUpEnabled(e: { value: boolean }): void {
        this.ftUpEnabled = e.value === true;
        this.emit();
    }

    onFtDownEnabled(e: { value: boolean }): void {
        this.ftDownEnabled = e.value === true;
        this.emit();
    }

    onFtUpMinProfitMode(e: { itemData: { id: 'normalized' | 'raw' } }): void {
        const toNormalized = e.itemData.id === 'normalized';
        // CR-20260901-001: same flip-converts-the-amount rule as the global selector above.
        if (toNormalized !== this.ftUpMinProfitNormalized) {
            this.ftUpMinProfit = convertOnNormalizedModeFlip(this.ftUpMinProfit, toNormalized, this.defaultQty);
        }
        this.ftUpMinProfitNormalized = toNormalized;
        this.emit();
    }

    onFtDownMinProfitMode(e: { itemData: { id: 'normalized' | 'raw' } }): void {
        const toNormalized = e.itemData.id === 'normalized';
        // CR-20260901-001: same flip-converts-the-amount rule as the global selector above.
        if (toNormalized !== this.ftDownMinProfitNormalized) {
            this.ftDownMinProfit = convertOnNormalizedModeFlip(this.ftDownMinProfit, toNormalized, this.defaultQty);
        }
        this.ftDownMinProfitNormalized = toNormalized;
        this.emit();
    }

    // "Adjust to Expected Range" (CR-20260820-002). TICKING IT CLEARS BOTH STRIKES, by the owner's
    // own description: *"these two start and end strikes become unavailable and reset their
    // values... that's a mutual exclusive choice"*.
    //
    // ⚠ CLEARED, NOT JUST DISABLED. A greyed dropdown still holding 7700 says the run will use 7700
    // when it will not, and the value would come back the instant the box is unticked - so the
    // panel would be showing a range nobody asked for and the trader would have no reason to doubt
    // it. Unticking leaves them empty, which is honest: the trader has not chosen yet.
    onExpectedRange(e: { value: boolean }): void {
        this.useExpectedRange = e.value === true;
        this.clearStrikesWhilePairIsDead();
        this.emit();
    }

    /**
     * AN INVARIANT, NOT A ONE-SHOT: while the strike pair is disabled by this box, the pair must be
     * EMPTY. Called from every control that can put the panel into that state.
     *
     * ⚠ It has to run on the FORM SWITCH too, not just on the tick, because the pair is SHARED.
     * Range(tick, strikes cleared) -> Fill Gap (box unmounts, the flag survives, the pair comes back
     * to life) -> type 7600/7800 -> Range again would otherwise leave the two dropdowns DISABLED but
     * still displaying 7600 and 7800, while the run measured its own band and adjusted somewhere
     * else entirely. The trader would be reading two numbers off the screen that the run never used.
     * (The Follow-Through stopped sharing this pair with its CR-20260824-015 rewrite - its sides
     * carry their own strikes.)
     */
    private clearStrikesWhilePairIsDead(): void {
        if (this.strikePairDisabled) {
            this.startStrike = null;
            this.endStrike = null;
        }
    }

    buildState(): RiskInputsState {
        const priceRange = this.buildPriceRange();
        const maxRisk = this.buildMaxRisk();
        // THE GLOBAL MINIMUM IS HIDDEN ON THE FOLLOW-THROUGH (CR-20260824-015): each side carries
        // its own, inside the price range, and a hidden control's value must never travel — so the
        // run-level figure is reported at 0 and its validity term is the price range's own
        // (isPriceRangeValid demands a positive minimum per enabled side).
        const minProfit = this.isFollowThrough ? 0 : orNaN(this.minProfit);
        const valid = isPriceRangeValid(priceRange)
            && isFinite(minProfit) && minProfit >= 0
            && isMaxRiskCeilingValid(maxRisk);
        // Gated on allowHarvest, not just on the field: a consumer that never shows the control must
        // never send true, even if a future state-seed @Input sets the field behind its back.
        // And never on the Follow-Through — the global control is hidden there; the per-side
        // commands ride inside the price range.
        const harvest = !this.isFollowThrough && this.allowHarvest && this.harvest;
        // Gated on VISIBILITY, not just on the fields, for the same reason harvest is: while the
        // controls are hidden the trader is not answering these questions, so their last answer must
        // not travel. Defaults out = both sides, full risk, which is the shipped behaviour.
        //
        // THE SIDE TRAVELS WHENEVER ITS CONTROLS ARE LIVE (CR-20260819-002 R-9, superseding
        // CR-20260818-001's "the side and the halving qualify the RELATIVE ceiling"): relative
        // non-Low (the existing gate) OR the dollar arm with the section visible — the dollar tab
        // offers the same selector now, on every dollar level. halfRisk stays RELATIVE non-Low
        // ONLY (R-3): the dollar arm has no half-risk control, so dollar mode always sends false.
        //
        // ⚠ The relative gate is showRiskSideControls, NOT showRelativeLevels: on Low the controls
        // are still MOUNTED (the watermark only covers them, to hold the height), so the fields
        // still hold whatever the trader last picked. Reading the fields there would put a side and
        // a halving on the wire that nothing on screen states — the exact thing this gate exists to
        // stop. The fields are deliberately left alone so the choice comes back when they leave Low
        // (his instruction: "maintain state").
        const sideControlsLive = this.showRiskSideControls || this.showDollarLevels;
        const riskSide: RiskSide = sideControlsLive ? this.riskSide : 'both';
        const halfRisk = this.showRiskSideControls && this.halfRisk;
        // Gated on the same EFFECTIVE flag the price range is, so a model can never travel for a
        // run that is not asking for a band — and, on a run that is, can never be absent.
        const expectedRangeModel = this.useExpectedRangeEffective
            ? this._expectedMove.getCoPilotRangeModel()
            : null;

        return {
            priceRange, minProfit, minProfitNormalized: this.minProfitNormalized, maxRisk,
            riskSide, halfRisk, harvest, expectedRangeModel, valid
        };
    }

    private buildPriceRange(): PriceRange {
        if (this.priceKind === 'bounded') {
            // Gated on the EFFECTIVE flag, not the raw field, for the same reason `harvest` is: a
            // question the trader cannot see themselves answering must never travel. The strikes
            // are reported as NaN rather than as whatever the cleared boxes hold, so the mapper has
            // one thing to recognise and the wire carries no number nobody chose.
            if (this.useExpectedRangeEffective) {
                return {
                    kind: 'bounded', startStrike: NaN, endStrike: NaN, useExpectedRange: true
                };
            }
            return { kind: 'bounded', startStrike: orNaN(this.startStrike), endStrike: orNaN(this.endStrike) };
        }
        if (this.priceKind === 'fillGap') {
            return { kind: 'fillGap', startStrike: orNaN(this.startStrike), endStrike: orNaN(this.endStrike) };
        }
        if (this.priceKind === 'followThrough') {
            // CR-20260824-015: a DISABLED side travels null — its fields keep whatever was typed
            // (so re-enabling restores them) but a value the trader cannot see must never travel.
            return {
                kind: 'followThrough',
                up: this.ftUpEnabled
                    ? {
                        startStrike: orNaN(this.ftUpStrike),
                        minProfit: orNaN(this.ftUpMinProfit),
                        minProfitNormalized: this.ftUpMinProfitNormalized,
                        harvest: this.ftUpHarvest
                    }
                    : null,
                down: this.ftDownEnabled
                    ? {
                        startStrike: orNaN(this.ftDownStrike),
                        minProfit: orNaN(this.ftDownMinProfit),
                        minProfitNormalized: this.ftDownMinProfitNormalized,
                        harvest: this.ftDownHarvest
                    }
                    : null,
                closeRemaining: this.ftCloseRemaining
            };
        }
        return { kind: 'wall' };
    }

    private buildMaxRisk(): MaxRiskCeiling {
        // THE WHOLE SECTION IS HIDDEN OFF THE RANGE FORM, SO NOTHING IN IT MAY TRAVEL. [my call, marked]
        //
        // Two things break if the last-clicked mode is left to speak here. A Dollar amount half
        // typed on a Range and then abandoned for a Wall is INVALID (isMaxRiskCeilingValid rejects
        // NaN), so Run would sit disabled with nothing on screen to fix — the trader cannot see the
        // control that is blocking them. And a level picked on a Range would still deepen a
        // Follow-Through, which is exactly what hiding it was meant to stop.
        //
        // Low is the honest substitute: it is the level that adds no risk, which is what these forms
        // did before any level existed.
        if (!this.showMaxRisk) {
            return { kind: 'relative', level: 'Low' };
        }
        if (this.maxRiskKind === 'dollar') {
            // The dollar arm's own level rides along (CR-20260819-002 R-2). The field can never
            // hold 'Low' — DollarRiskLevel excludes it by type — so no run can ask the backend for
            // the placement it refuses by name.
            return {
                kind: 'dollar', amount: orNaN(this.dollarAmount),
                normalized: this.dollarNormalized, level: this.dollarLevel
            };
        }
        // The level is sent as picked. There is no "hidden ⇒ Low" branch HERE any more: the section
        // is hidden as a whole, and the early return above is the one place that decides what a
        // hidden section says. A second guard on the same rule would read as two rules.
        //
        // Moderate is sent as Moderate. It falls back to the Aggressive shape in the engine for now,
        // but the wire and the log should say what the trader actually picked — rewriting it here
        // would put a level in the trace nobody chose, and the trace is where a report gets diagnosed.
        return { kind: 'relative', level: this.relativeLevel };
    }
}
